import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactElement } from "react";
import type { TicketBoardCard, TicketKarte, Unternehmen } from "@kundeninfo/api-client";
import { GROBER_ZEIGER, Icon, useMedienabfrage, useSofortPopup, useTelefon } from "@kundeninfo/ui";
import { Chip } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { anhangZaehler, berlinerTag, datumsMarke, tageZwischen } from "./ticketFormat.js";
import { Avatar } from "./Avatar.js";
import { TeamChip } from "./TeamChip.js";
import { QueueBadge } from "./OwnerBadge.js";
import { SubtaskRail } from "./SubtaskRail.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { EffortMark } from "./EffortMark.js";
import { hasEffort, shownEffort } from "./effort.js";
import { STRIPE_PX, cardName, dueMark, numberPopup, strokeCount } from "./hierarchy.js";

/**
 * DIE TICKET-KARTE (Spec „Ticket-Karte"): Objektzeilen-Optik als Übergang T-1 (me-objektzeile
 * trägt die Board-Karte nicht), Art-Symbol zuerst, der Zuständige als Avatar mit Namen (F48,
 * me-avatar). Titel, Namen und Stand aus Fremdsystemen sind reiner Text (React escaped,
 * kein rohes HTML).
 *
 * Interaktion ohne Verschachtelung (Review 12): Die Karte selbst ist ein nicht-interaktiver
 * `article` (Name aus Nummer und Titel, Beschreibung aus Datum und Zuständigem). Nummer und
 * Titel SIND der Öffnen-Knopf; sein Pseudo-Element deckt die ganze Karte (Klick irgendwo
 * öffnet), Griff, Avatar, Kunden-Chip und „Verschieben …" (Symbolknopf rechts unten) liegen
 * per z-index darüber und handeln selbst. Der Griff ⠿ ist ein Knopf mit `data-griff` für die
 * Ziehhilfen aus me-design (F126): Zeiger, Finger (Langdruck) und Tastatur (Leertaste nimmt auf);
 * seine Optik setzt der Helfer über `data-me-griff`. „Verschieben …" (F22) bleibt der zweite Weg — am Telefon (E457) der einzige:
 * dort fehlt der Griff, die Karte hat eine Spalte (`tk--nohandle`) und der Inhalt beginnt hinter dem Trefferstreifen.
 *
 * Teilaufgaben (Spec Teilaufgaben R11, R24, R25, R27): Striche am linken Rand nach Tiefe (der erste ist der Kartenrand),
 * darüber der Trefferstreifen ab der Außenkante — Überfahren und Tastaturfokus öffnen die Randliste, Klick, Enter und
 * Leertaste schalten den Fokus (R26). Am Telefon und bei grobem Zeiger (v006: „Telefon oder Touch") ist der Streifen
 * `--treffer` breit, Überfahren und Fokus öffnen nichts, Tippen öffnet und schließt die Randliste. Der Kopf zeigt die
 * Anzeige-Nummer, ihr Sofort-Popup die echte; der Name der Karte folgt R24 (`cardName`), die echte Nummer bleibt in
 * `data-nummer`.
 */

const SYSTEM: Record<string, string> = { GITLAB: "GitLab", YOUTRACK: "YouTrack", URL: "Link" };
/** Art des Tickets mit Symbol — eine Zuordnung für Karte und Arbeitsdialog (TicketDialog.tsx). */
export const ART: Record<TicketKarte["kind"], { symbol: string; name: string }> = {
  TASK: { symbol: "square-check", name: "Aufgabe" },
};

export type TicketKarteProps = {
  t: TicketBoardCard; heute: string; ichId: string; companies: readonly Unternehmen[];
  onOeffnen: (id: string) => void; onVerschieben: (t: TicketKarte) => void;
  /** Kennungen auf dem Board (für „+n außerhalb des Filters", R25). */
  shown?: ReadonlySet<string>;
  focusRole?: "main" | "child" | null;
  /** Klick auf die Striche (R26). */
  onToggleFocus?: (id: string) => void;
  /** Titel des Elterntickets für das Popup „Ebene n · Teil von …". */
  parentTitle?: string | null;
  /** Diese Karte ist die Hauptkarte des Fokus (Kopf der Randliste am Telefon, R27). */
  focused?: boolean;
  onShowInBoard?: (id: string) => void;
};

export function TicketKarteAnsicht(p: TicketKarteProps): ReactElement {
  const t = p.t;
  const id = useId();
  const marke = datumsMarke(t.resubmitDate, p.heute, t.resubmitTime);
  const anhaenge = anhangZaehler(t.attachmentCounts);
  const erster = t.customers[0];
  const kundenPopup = useSofortPopup<HTMLSpanElement>(
    t.customers.length === 0 ? "" : `${t.companyShortCode} · ${t.customers.map((c) => c.name).join(", ")}`);
  const verschiebenPopup = useSofortPopup<HTMLSpanElement>("Verschieben …");
  const tasten = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); p.onOeffnen(t.id); }
  };
  const art = ART[t.kind];
  const link = t.firstLink;

  const telefon = useTelefon();
  const grob = useMedienabfrage(GROBER_ZEIGER) || telefon;
  const striche = strokeCount(t);
  const streifenBreite = grob ? "var(--treffer)" : `${STRIPE_PX[striche]}px`;
  const mitKindern = t.subtaskTotal > 0;
  const [randliste, setRandliste] = useState<{ rect: DOMRect; el: HTMLElement } | null>(null);
  const gesperrt = useRef(false);   // nach Klick zu, bis der Zeiger den Streifen verließ (R25) bzw. der Fokus ging
  const ueberListe = useRef(false); // der Zeiger steht über der Liste: der Blur des Streifens schließt nicht
  const zuTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streifenRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => () => { if (zuTimer.current !== null) clearTimeout(zuTimer.current); }, []);
  const oeffneListe = () => {
    if (zuTimer.current !== null) clearTimeout(zuTimer.current);
    const el = streifenRef.current;
    if (!mitKindern || gesperrt.current || el === null) return;
    setRandliste((offen) => offen ?? { rect: el.getBoundingClientRect(), el });
  };
  const schliesseListe = useCallback((sofort = false) => {
    if (zuTimer.current !== null) clearTimeout(zuTimer.current);
    const zu = () => { ueberListe.current = false; setRandliste(null); };
    if (sofort) zu();
    else zuTimer.current = setTimeout(zu, 150);   // Sofort-Popup-Mechanik: Verzögerung nur beim Schließen
  }, []);
  const schliesseSofort = useCallback(() => schliesseListe(true), [schliesseListe]);
  const streifenPopup = useSofortPopup<HTMLSpanElement>(!mitKindern && t.parentTicketNumber !== null
    ? HIERARCHY_TEXT.levelOf(t.depth + 1, t.parentTicketNumber, p.parentTitle ?? null) : "");
  const streifenRefs = useCallback((el: HTMLSpanElement | null) => { streifenRef.current = el; streifenPopup(el); },
    [streifenPopup]);
  const umschalten = () => {
    if (!mitKindern) return;
    // R27, v006: Tippen öffnet die Randliste ohne Fokus, ein zweites Tippen schließt sie.
    if (grob) { if (randliste === null) oeffneListe(); else schliesseListe(true); return; }
    gesperrt.current = true;
    schliesseListe(true);
    p.onToggleFocus?.(t.id);
  };
  const nummerPopup = useSofortPopup<HTMLSpanElement>(numberPopup(t));
  const faellig = dueMark(t.dueDate, p.heute, t.stateKind === "DONE");
  const aufwand = hasEffort(shownEffort(t));
  const warteschlange = t.owner.kind === "TEAM" && t.owner.team !== null && t.assignee === null ? t.owner.team : null;
  return (
    <article className={telefon ? "tk tk--nohandle" : "tk"} data-ticket={t.id} data-nummer={t.ticketNumber} data-strokes={striche}
      data-focus={p.focusRole ?? undefined} aria-label={cardName(t)} aria-describedby={`${id}-datum ${id}-wer${aufwand ? ` ${id}-aufwand` : ""}`}
      {...(striche > 0 ? { style: { paddingLeft: streifenBreite } } : {})}>
      {/* Trefferstreifen (R24, Festlegung 5): ab der Außenkante über Rand und Striche; nur mit eigenen Kindern ein Knopf. */}
      {striche > 0 && (
        <span ref={streifenRefs} className={`tk__streifen${randliste !== null ? " tk__streifen--aktiv" : ""}`}
          style={{ width: streifenBreite }}
          {...(mitKindern ? { role: "button", tabIndex: 0, "aria-pressed": p.focusRole === "main",
                               "aria-label": HIERARCHY_TEXT.railName(t.displayNumber) } : { "aria-hidden": true })}
          onClick={umschalten}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); umschalten(); } }}
          {...(grob ? {} : { onMouseEnter: oeffneListe, onFocus: oeffneListe,
                               onMouseLeave: () => { gesperrt.current = false; schliesseListe(); } })}
          onBlur={() => { gesperrt.current = false; if (!ueberListe.current) schliesseListe(); }} />
      )}
      {randliste !== null && (
        <SubtaskRail t={t} anchor={randliste.rect} anchorEl={randliste.el} ichId={p.ichId} heute={p.heute} coarse={grob}
          shown={p.shown ?? new Set(t.subtasks.map((z) => z.id))} focused={p.focused === true} onOeffnen={p.onOeffnen}
          onPointerEnter={() => { ueberListe.current = true; if (!grob) oeffneListe(); }}
          onPointerLeave={() => { ueberListe.current = false; if (!grob) schliesseListe(); }} onClose={schliesseSofort}
          {...(p.onShowInBoard === undefined ? {} : { onShowInBoard: p.onShowInBoard })}
          {...(p.onToggleFocus === undefined ? {} : { onEndFocus: () => p.onToggleFocus!(t.id) })} />
      )}
      {/* Der Griff ⠿ (F126): gezogen wird NUR von hier, nie von der ganzen Karte (Langdruck 350 ms hebt an). Tabstopp für den
          Tastaturweg; den Namen setzt auch der Helfer („Ticket <Nummer> verschieben"). E457: Am Telefon gibt es ihn nicht. */}
      {!telefon && (
        <button type="button" className="tk__griff tk__oben" data-griff="" aria-label={`Ticket ${t.ticketNumber} verschieben`}>
          <span aria-hidden="true">⠿</span>
        </button>
      )}
      <div className="tk__kopf">
        <Icon name={art.symbol} label={art.name} className="tk__art" />
        <span className="tk__oeffnen" role="button" tabIndex={0}
          onClick={() => p.onOeffnen(t.id)} onKeyDown={tasten}>
          <span ref={nummerPopup} className="tk__nummer">{t.displayNumber}</span>{" "}
          <span className="tk__titel">{t.title}</span>
        </span>
        {/* Das Datum rechts neben dem Titel (Entwurf v001): der Titel bekommt die Breite. */}
        <span id={`${id}-datum`} className="tk__datumfeld">
          {marke.art === null
            ? <Chip className="tk__datum">{marke.text}</Chip>
            : <Chip className="tk__datum" statusArt={marke.art}>{marke.text}</Chip>}
        </span>
      </div>
      <div className="tk__zeile">
        {/* Über dem Öffnen-Knopf, damit das Popup mit allen Kunden erscheint; ein Klick öffnet
            trotzdem (nur Zeiger — die Tastatur öffnet über Nummer und Titel). */}
        <span ref={kundenPopup} className="tk__kundefeld tk__oben" onClick={() => p.onOeffnen(t.id)}>
          <Chip className="tk__kunde" {...hausfarbeChip(p.companies, t.companyShortCode)}>
            <span className="tk__kundetext">
              {erster === undefined ? t.companyShortCode : `${t.companyShortCode} · ${erster.name}`}
            </span>
          </Chip>
        </span>
        {t.customers.length > 1 && <span className="tk__mehr">+{t.customers.length - 1}</span>}
        {/* F134: privat — Schloss und Kürzel; über dem Öffnen-Knopf (`tk__oben`), damit das Popup erscheint. */}
        <TeamChip team={t.team} className="tk__oben" />
        {t.contacts[0] !== undefined && <span className="tk__neben tk__kontakt">{t.contacts[0].name}</span>}
        {/* R24: rechts „2/3" und „fällig Fr 30.10." (R15) als rahmenlose Kennzeichen. */}
        {(mitKindern || faellig !== null || aufwand) && (
          <span className="tk__hierarchie">
            {mitKindern && <Chip className="tk__fortschritt">{HIERARCHY_TEXT.progress(t.subtaskDone, t.subtaskTotal)}</Chip>}
            {aufwand && <EffortMark card={t} id={`${id}-aufwand`} className="tk__aufwand tk__oben" />}
            {faellig !== null && (faellig.art === null
              ? <Chip className="tk__faellig">{faellig.text}</Chip>
              : <Chip className="tk__faellig" statusArt={faellig.art}>{faellig.text}</Chip>)}
          </span>
        )}
      </div>
      <div className="tk__fuss">
        {/* Antippen zeigt den Steckbrief (F48) und öffnet NICHT das Ticket. */}
        <span id={`${id}-wer`} className="tk__wer tk__oben">
          {/* E442, Festlegung 23: ein Team-Ticket ohne Zuständigen zeigt das Team als Warteschlange. */}
          {warteschlange !== null
            ? <QueueBadge team={warteschlange} />
            : <Avatar person={t.assignee} ich={t.assignee !== null && t.assignee.id === p.ichId} />}
        </span>
        <span className="tk__neben">{tageZwischen(berlinerTag(t.createdAt), p.heute)} T</span>
        {t.commentCount > 0 && (
          <span className="tk__neben">{t.commentCount === 1 ? "1 Kommentar" : `${t.commentCount} Kommentare`}</span>
        )}
        {/* Die Anhangzähler (F94) direkt neben den Kommentaren: je Art ein Symbol mit Zahl, für
            die Vorlesestimme ein Name je Art („2 PDF-Anhänge"). 14 px und schlichte Formen (Befund 7,
            Task 13): Das PDF-Symbol mit eingeschriebenem „PDF" war bei 12 px nur ein Fleck. */}
        {anhaenge.length > 0 && (
          <span className="tk__neben tk__anhaenge">
            {anhaenge.map((a) => (
              <span key={a.art} className={`tk__anhang tk__anhang--${a.art}`} role="img" aria-label={a.name}>
                <Icon name={a.symbol} size={14} /> {a.anzahl}
              </span>
            ))}
          </span>
        )}
        {link !== null && (
          <span className="tk__link">
            {link.systemType === "URL" ? "Link" : `${SYSTEM[link.systemType]} ${link.externalKey}`}
            {link.remoteState !== null ? `: ${link.remoteState}` : ""}
            {t.linkCount > 1 ? ` +${t.linkCount - 1}` : ""}
          </span>
        )}
        {/* „Verschieben …“ als Symbolknopf rechts in der Fußzeile (Entwurf v001, Sichtprobe 3):
            der Name „<Nummer> verschieben …“ (Auslassung: öffnet den Dialog; anders als der Griff
            „Ticket <Nummer> verschieben“), das Sofort-Popup nennt ihn fürs Auge. */}
        <span ref={verschiebenPopup} className="tk__aktion tk__oben">
          <KiKnopf art="leise" className="tk__verschieben" name={`${t.ticketNumber} verschieben …`}
            onClick={() => p.onVerschieben(t)}>
            <Icon name="arrows-up-down-left-right" size={14} />
          </KiKnopf>
        </span>
      </div>
    </article>
  );
}
